import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useFormState } from 'react-use-form-state'
import { Mutation } from 'react-apollo'

import Button from '../../../../../../components/button'
import { getValidity, Text, Toggle } from '../../../../../../components/form'
import {
  getEnv,
  pushGAEvent,
  normalizeSvensktPersonnummer,
  normalizePhoneNumber,
  validatePhoneNumber,
  validateSvensktPersonnummer,
} from '../../../../../../lib/utils'

import {
  DooerComSignup,
  DooerComSignupVariables,
} from '../../../../../../components/functional/basic-signup/bankid-session/__generated__/DooerComSignup'
import { startBankIdSession } from '../../../../../../components/functional/basic-signup/bankid-session/gql'
import BankidSessionStatus from '../../../../../../components/functional/basic-signup/bankid-session'
import messages from '../../../../../../components/functional/basic-signup/form/messages'
import { ReactComponent as BankIdSvg } from '../../../../../../components/functional/basic-signup/form/bankid.svg'

import styles from './form.module.scss'

interface IFormValues {
  companyName: string
  ecommerceLevel: 'start' | 'standard' | 'premium'
  email: string
  cellPhone: string
  firstName: string
  lastName: string
  personnummer: string
  integrity: boolean
  termsOfService: boolean
}

interface IFormProps {
  header?: React.ReactNode
}

function Form({ header }: IFormProps) {
  // Use intl HOC
  const intl = useIntl()

  const errorMessages = {
    personnummer: intl.formatMessage(messages.errorPersonnummer),
    email: intl.formatMessage(messages.errorEmail),
    emailMismatch: intl.formatMessage(messages.errorEmailMismatch),
    integrity: intl.formatMessage(messages.errorIntegrity),
    termsOfService: intl.formatMessage(messages.errorTermsOfService),
    text: intl.formatMessage(messages.errorText),
    cellPhone: intl.formatMessage(messages.errorCellPhone),
  }

  const [bankidSessionId, setBankidSessionId] = React.useState<string | undefined>()
  const [formState, input] = useFormState<IFormValues>(
    {
      ecommerceLevel: 'standard',
    },
    {
      withIds: true,
    }
  )

  return (
    <div className={styles.root}>
      <Mutation<DooerComSignup, DooerComSignupVariables> mutation={startBankIdSession}>
        {(mutate, { loading, error }) => (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (Object.keys(formState.errors).length > 0) {
                return false
              }

              mutate({
                variables: {
                  sePersonnummer: normalizeSvensktPersonnummer(formState.values.personnummer),
                  input: {
                    companyName: formState.values.companyName,
                    firstName: formState.values.firstName,
                    lastName: formState.values.lastName,
                    email: formState.values.email,
                    cellPhone: normalizePhoneNumber(formState.values.cellPhone),
                    apps: ['accounting'],
                  },
                },
              }).then((res) => {
                if (res && res.data) {
                  setBankidSessionId(res.data.signupWithBankId.id)
                }
              })
            }}
          >
            {bankidSessionId ? (
              <div className={styles.bankIDWrapper}>
                <BankIdSvg style={{ height: '120px' }} />
                <BankidSessionStatus
                  onComplete={(data) => {
                    pushGAEvent('signup-accounting')
                    window.location.href = `https://go.${getEnv()}.com/login?token=${data.token}`
                  }}
                  onFailure={() => setBankidSessionId(undefined)}
                  sessionId={bankidSessionId}
                />
                <div className={styles.preloader} />
              </div>
            ) : (
              <div>
                {header}
                <div className={styles.inputs}>
                  <Text
                    errorCollapse="dynamic"
                    errorMessage={errorMessages.text}
                    input={input}
                    name="companyName"
                    placeholder={intl.formatMessage(messages.companyName)}
                    required
                    valid={getValidity(formState, 'companyName')}
                  />

                  <Text
                    errorCollapse="dynamic"
                    errorMessage={errorMessages.text}
                    input={input}
                    name="firstName"
                    placeholder={intl.formatMessage(messages.firstName)}
                    required
                    valid={getValidity(formState, 'firstName')}
                  />

                  <Text
                    errorCollapse="dynamic"
                    errorMessage={errorMessages.text}
                    input={input}
                    name="lastName"
                    placeholder={intl.formatMessage(messages.lastName)}
                    required
                    valid={getValidity(formState, 'lastName')}
                  />

                  <Text
                    // SMELL: apollo-boost swallows the error before we can see it, so we can't know for sure that this is the exact error
                    errorCollapse="dynamic"
                    errorMessage={
                      error &&
                      error.graphQLErrors &&
                      error.graphQLErrors.some(
                        (err) => err.extensions && err.extensions.code === 'signup-email-mismatch'
                      )
                        ? errorMessages.emailMismatch
                        : errorMessages.email
                    }
                    input={input}
                    name="email"
                    placeholder={intl.formatMessage(messages.email)}
                    required
                    type="email"
                    valid={error ? 'invalid' : getValidity(formState, 'email')}
                  />

                  <Text
                    errorCollapse="dynamic"
                    errorMessage={errorMessages.cellPhone}
                    input={input}
                    name="cellPhone"
                    placeholder={intl.formatMessage(messages.cellPhone)}
                    required
                    type="tel"
                    valid={getValidity(formState, 'cellPhone')}
                    validate={(value: string) =>
                      validatePhoneNumber(normalizePhoneNumber(value)) ? true : errorMessages.cellPhone
                    }
                  />

                  <Text
                    errorCollapse="dynamic"
                    errorMessage={errorMessages.personnummer}
                    input={input}
                    name="personnummer"
                    placeholder={intl.formatMessage(messages.personnummer)}
                    required
                    valid={getValidity(formState, 'personnummer')}
                    validate={(value: string) =>
                      validateSvensktPersonnummer(normalizeSvensktPersonnummer(value))
                        ? true
                        : errorMessages.personnummer
                    }
                    validateOnBlur
                  />
                </div>

                <div className={styles.toggles}>
                  <Toggle
                    checked={formState.values.integrity}
                    errorMessage={errorMessages.integrity}
                    input={input}
                    label={
                      <>
                        {intl.formatMessage(messages.integrity)}{' '}
                        <a href="/gdpr" target="_blank">
                          <FormattedMessage {...messages.readMore} />
                        </a>
                      </>
                    }
                    name="integrity"
                    required
                    valid={getValidity(formState, 'integrity')}
                  />

                  <Toggle
                    checked={formState.values.termsOfService}
                    errorMessage={errorMessages.termsOfService}
                    input={input}
                    label={
                      <>
                        {intl.formatMessage(messages.termsOfService)}{' '}
                        <a href="/tos" target="_blank">
                          <FormattedMessage {...messages.readMore} />
                        </a>
                      </>
                    }
                    name="termsOfService"
                    required
                    valid={getValidity(formState, 'termsOfService')}
                  />
                </div>

                <Button bankId block className={styles.bankIDButton} disabled={loading} type="submit" variant="success">
                  <span className="visible-xs">
                    <FormattedMessage {...messages.submit} />
                  </span>
                  <span className="hidden-xs">
                    <FormattedMessage {...messages.submitShort} />
                  </span>
                </Button>
                <FormattedMessage tagName="p" {...messages.signUpInfo} />
              </div>
            )}
          </form>
        )}
      </Mutation>
    </div>
  )
}

export default Form
